import React from 'react';
import { useTranslation } from 'react-i18next';

const styles = {
  container: {
    position: 'fixed',
    right: '20px',
    top: '80px',
    width: '350px',
    maxHeight: 'calc(100vh - 100px)',
    background: 'rgba(0, 0, 0, 0.85)',
    backdropFilter: 'blur(10px)',
    borderRadius: '16px',
    padding: '20px',
    zIndex: 100,
    overflowY: 'auto',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    marginBottom: '20px',
    paddingBottom: '15px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  },
  flag: {
    width: '60px',
    height: '40px',
    objectFit: 'cover',
    borderRadius: '4px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
  },
  countryName: {
    fontSize: '24px',
    fontWeight: 'bold',
    margin: 0,
  },
  officialName: {
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.6)',
    marginTop: '4px',
  },
  infoGrid: {
    display: 'grid',
    gap: '15px',
  },
  infoItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  label: {
    fontSize: '12px',
    color: 'rgba(255, 255, 255, 0.5)',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  value: {
    fontSize: '16px',
    color: '#fff',
  },
  closeButton: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    background: 'rgba(255, 255, 255, 0.1)',
    border: 'none',
    color: '#fff',
    width: '30px',
    height: '30px',
    borderRadius: '50%',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
  },
  placeholder: {
    position: 'fixed',
    right: '20px',
    top: '80px',
    width: '350px',
    background: 'rgba(0, 0, 0, 0.85)',
    backdropFilter: 'blur(10px)',
    borderRadius: '16px',
    padding: '40px 20px',
    zIndex: 100,
    textAlign: 'center',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  placeholderText: {
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: '14px',
  },
};

const formatNumber = (num) => {
  return new Intl.NumberFormat().format(num);
};

const CountryInfo = ({ country, onClose }) => {
  const { t, i18n } = useTranslation();

  // Get localized name based on current language
  const getLocalizedName = (en, ru) => i18n.language === 'ru' ? (ru || en) : en;

  if (!country) {
    return (
      <div style={styles.placeholder}>
        <p style={styles.placeholderText}>
          {t('country.clickHint')}
        </p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <button style={styles.closeButton} onClick={onClose}>
        ×
      </button>
      <div style={styles.header}>
        {country.flag && (
          <img src={country.flag} alt={t('country.flagAlt', { country: getLocalizedName(country.name, country.nameRu) })} style={styles.flag} />
        )}
        <div>
          <h2 style={styles.countryName}>{getLocalizedName(country.name, country.nameRu)}</h2>
          <p style={styles.officialName}>{getLocalizedName(country.officialName, country.officialNameRu)}</p>
        </div>
      </div>
      <div style={styles.infoGrid}>
        <div style={styles.infoItem}>
          <span style={styles.label}>{t('country.capital')}</span>
          <span style={styles.value}>{country.capital}</span>
        </div>
        <div style={styles.infoItem}>
          <span style={styles.label}>{t('country.region')}</span>
          <span style={styles.value}>
            {country.region} {country.subregion && `• ${country.subregion}`}
          </span>
        </div>
        <div style={styles.infoItem}>
          <span style={styles.label}>{t('country.population')}</span>
          <span style={styles.value}>{formatNumber(country.population)}</span>
        </div>
        <div style={styles.infoItem}>
          <span style={styles.label}>{t('country.area')}</span>
          <span style={styles.value}>{formatNumber(country.area)} {t('country.areaUnit')}</span>
        </div>
        <div style={styles.infoItem}>
          <span style={styles.label}>{t('country.languages')}</span>
          <span style={styles.value}>{country.languages}</span>
        </div>
        <div style={styles.infoItem}>
          <span style={styles.label}>{t('country.currency')}</span>
          <span style={styles.value}>{country.currencies}</span>
        </div>
      </div>
    </div>
  );
};

export default CountryInfo;
